// ABOUTME: Ticket card component for warehouse service desk displaying ticket info and updates.
// ABOUTME: Shows ticket number, priority, relation info, and a list of recent updates.

import { Box, ButtonBase, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import Text from "../../../../../components/theme/text/Text";
import { tokens } from "../../../../../theme";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../components/theme/TabView3/IconColorGenerator";
import PriorityBadge from "./PriorityBadge";
import { generateHTML } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import TiptapText from "@tiptap/extension-text";
import Italic from "@tiptap/extension-italic";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import FontSize from "./chat/extensions/FontSize";
import FontWeight from "./chat/extensions/FontWeight";

// Same extension set the ChatInput editor and message bubbles use, so ticket
// descriptions render identically here (paragraph breaks, bold, italic, etc).
const DESCRIPTION_EXTENSIONS = [Document, Paragraph, TiptapText, Italic, TextStyle, Color, FontSize, FontWeight];
// Collapsed preview caps: at most 50 characters across at most 3 blocks (~3 lines).
const DESCRIPTION_PREVIEW_CHARS = 50;
const DESCRIPTION_PREVIEW_BLOCKS = 3;

// Descriptions are stored as a stringified TipTap doc (see MAddSupportTicket);
// older tickets may hold a raw string. Return the doc when we have one.
const parseDescriptionDoc = (description) => {
    if (!description) return null;
    if (typeof description === "object") return description.type === "doc" ? description : null;
    try {
        const doc = JSON.parse(description);
        return doc && doc.type === "doc" ? doc : null;
    } catch {
        return null;
    }
};

// Trim a TipTap doc to at most `maxChars` characters across at most `maxBlocks`
// top-level blocks, keeping node structure intact so it still renders with the
// right line breaks/formatting. Returns the clipped doc and whether anything was cut.
const truncateTiptapDoc = (doc, maxChars, maxBlocks) => {
    let remaining = maxChars;
    let truncated = false;
    const clip = (node) => {
        if (node.type === "text") {
            const text = node.text || "";
            if (text.length > remaining) {
                truncated = true;
                const clipped = { ...node, text: text.slice(0, remaining) };
                remaining = 0;
                return clipped;
            }
            remaining -= text.length;
            return node;
        }
        if (!node.content) return node;
        const content = [];
        for (const child of node.content) {
            if (remaining <= 0) { truncated = true; break; }
            content.push(clip(child));
        }
        return { ...node, content };
    };
    const blocks = doc.content || [];
    const kept = [];
    for (const block of blocks) {
        if (kept.length >= maxBlocks || remaining <= 0) { truncated = true; break; }
        kept.push(clip(block));
    }
    return { doc: { ...doc, content: kept }, truncated };
};

// Build collapsed + full previews for a ticket description, or null when empty.
// `isHtml` tells the card whether the strings are HTML (TipTap) or plain text.
const buildDescriptionPreview = (description) => {
    const doc = parseDescriptionDoc(description);
    if (doc) {
        try {
            const full = generateHTML(doc, DESCRIPTION_EXTENSIONS);
            const { doc: clipped, truncated } = truncateTiptapDoc(doc, DESCRIPTION_PREVIEW_CHARS, DESCRIPTION_PREVIEW_BLOCKS);
            const collapsed = truncated
                ? generateHTML(clipped, DESCRIPTION_EXTENSIONS).replace(/<\/p>\s*$/, "…</p>")
                : full;
            return { collapsed, full, truncated, isHtml: true };
        } catch {
            return null;
        }
    }
    // Legacy plain-text description (not TipTap JSON).
    if (typeof description === "string") {
        const trimmed = description.trim();
        if (trimmed && !trimmed.startsWith("{")) {
            const truncated = trimmed.length > DESCRIPTION_PREVIEW_CHARS;
            const collapsed = truncated ? trimmed.slice(0, DESCRIPTION_PREVIEW_CHARS).trimEnd() + "…" : trimmed;
            return { collapsed, full: trimmed, truncated, isHtml: false };
        }
    }
    return null;
};

export const ServicedeskTicketCardWarehouse = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const descriptionPreview = useMemo(
        () => buildDescriptionPreview(props.ticket.description),
        [props.ticket.description]
    );

    return (
        <ButtonBase
            onClick={props.onClick}
            TouchRippleProps={{
                style: {
                    color: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[300]
                }
            }}
            sx={{
                width:1,
                pl:4,
                pr:3,
                py:3,
                background: props.selected
                    ? (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50])
                    : (theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]),
                borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                display:"flex",
                gap:1.5,
                justifyContent:'flex-start',
                alignItems:'flex-start',
                transition: 'background 0.2s',
                ...props.sx,
            }}
        >
            <Box
                sx={{
                    width:32,
                    height:32,
                    flexShrink:0,
                    borderRadius:4,
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center',
                    position:'relative',
                    background: generateColorFromTitle(props.ticket.type ?? "", theme.palette.mode, 90),
                    "&:after" : {
                        content:"''",
                        position:'absolute',
                        left:0,
                        right:0,
                        top:0,
                        bottom:0,
                        borderRadius:4,
                        zIndex:-1,
                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0]
                    }
                }}
            >
                <Box
                    sx={{
                        width:20,
                        height:20,
                        flexShrink:0,
                        borderRadius:2.5,
                        background: generateIconColorFromTitle(props.ticket.type ?? "", theme.palette.mode),
                    }}
                />
            </Box>


            <Box
                sx={{
                    flex:1,
                    overflow:'hidden',
                    display:'flex',
                    flexDirection:'column',
                }}
            >
                {/* Ticket number and time */}
                <Box
                    sx={{
                        display:'flex',
                        width:1,
                        justifyContent:'space-between',
                        mb:0.5,
                    }}
                >
                    <Box
                        sx={{
                            display:'flex',
                            gap:1.5,
                            alignItems:'center',
                        }}
                    >
                        <Text bold>#{props.ticket.ticketNumber}</Text>

                        {props.ticket.priority === 'high' && (
                            <Text variant="red" fontSize="17" bold>!</Text>
                        )}
                    </Box>
                    <Box>
                        <Text light semibold>{props.ticket.lastUpdateTime}</Text>
                    </Box>
                </Box>

                {/* Title */}
                <Box
                    sx={{
                        overflow:"hidden",
                        width:1,
                    }}
                >
                    <Text noBreak sx={{display:"block"}}>{props.ticket.title}</Text>
                </Box>

                {/* Relation and contact */}
                {(props.ticket?.relationName && props.ticket.contactName) && (
                    <Box
                        sx={{
                            overflow:"hidden",
                            width:1,
                        }}
                    >
                        <Text light noBreak sx={{display:"block"}}>
                            {props.ticket.relationName} {props.ticket.contactName !== "Unknown" && <>/ {props.ticket.contactName}</>}
                        </Text>
                    </Box>

                )}

                {/* Description preview — rendered like the message bubbles, in a box.
                    Collapsed to ~3 lines / 50 chars with a show more/less toggle. */}
                {descriptionPreview && (
                    <Box
                        sx={{
                            mt:2,
                            width:1,
                            p:1.5,
                            borderRadius:2,
                            background: theme.palette.mode === "dark" ? props.selected ? colors.grey[400] : colors.grey[300] : colors.grey[100],
                            border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                            overflow:"hidden",
                            textAlign:"left",
                        }}
                    >
                        <Box
                            sx={{
                                fontSize:13,
                                lineHeight:1.4,
                                fontFamily:"Roboto, sans-serif",
                                color:colors.txt["primary"],
                                overflow:"hidden",
                                "& p": { margin: 0 },
                                "& p:empty::before": { content: '"\\00a0"' },
                                ...(descriptionPreview.isHtml ? {} : { whiteSpace:"pre-line" }),
                                // ~3 lines at lineHeight 1.4 while collapsed.
                                ...(descriptionExpanded ? {} : { maxHeight:"4.2em" }),
                            }}
                            {...(descriptionPreview.isHtml
                                ? { dangerouslySetInnerHTML: { __html: descriptionExpanded ? descriptionPreview.full : descriptionPreview.collapsed } }
                                : { children: descriptionExpanded ? descriptionPreview.full : descriptionPreview.collapsed })}
                        />
                        {descriptionPreview.truncated && (
                            <Box
                                component="span"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); setDescriptionExpanded((v) => !v); }}
                                sx={{
                                    display:"inline-block",
                                    mt:1,
                                    cursor:"pointer",
                                    fontSize:13,
                                    fontWeight:500,
                                    color:colors.txt["secondary"],
                                    "&:hover": { color:colors.txt["primary"] },
                                }}
                            >
                                {descriptionExpanded ? t("show_less") : t("show_more")}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Updates list */}
                {props.ticket.updates && props.ticket.updates.length > 0 && (
                    <Box
                        sx={{
                            mt:2,
                            display:"flex",
                            gap:1,
                            flexDirection:"column",
                        }}
                    >
                        {props.ticket.updates.map((update, index) => {
                            if (update.type === 'text') {
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            p:1.5,
                                            borderRadius:2,
                                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                                            border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                        }}
                                    >
                                        <Text fontSize={13} light>
                                            {update.content}
                                        </Text>
                                    </Box>
                                );
                            }

                            if (update.type === 'image') {
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            height:32,
                                            borderRadius:4,
                                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                                            border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                            pl:0.5,
                                            pr:2,
                                            display:"flex",
                                            alignItems:'center',
                                            gap:1.5,
                                        }}
                                    >
                                        <img
                                            style={{
                                                height:"24px",
                                                minWidth:"24px",
                                                objectFit:"cover",
                                                objectPosition:"center",
                                                borderRadius:"12px"
                                            }}
                                            src={update.imageUrl}
                                            alt={update.fileName}
                                        />
                                        <Text noBreak fontSize={13}>
                                            {update.fileName}
                                        </Text>
                                    </Box>
                                );
                            }

                            if (update.type === 'status_change') {
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            height:32,
                                            borderRadius:4,
                                            background: `linear-gradient(to right, ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0]} -15%, ${colors.green[200]} 150%), ${theme.palette.mode === "dark" ? "black" : "white"}`,
                                            border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                            pr:0.5,
                                            pl:2,
                                            display:"flex",
                                            alignItems:'center',
                                            justifyContent:"space-between",
                                            gap:1.5,
                                        }}
                                    >
                                        <Text noBreak fontSize={13}>
                                            {t("status_updated_to")}
                                        </Text>
                                        <Box
                                            sx={{
                                                flexShrink:0,
                                                height:24,
                                                px:1.5,
                                                borderRadius:3,
                                                background:colors.green[100],
                                                lineHeight:"24px",
                                            }}
                                        >
                                            <Text bold variant="green">{t(update.newStatus)}</Text>
                                        </Box>
                                    </Box>
                                );
                            }

                            if (update.type === 'reassignment') {
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            height:32,
                                            borderRadius:4,
                                            background: `linear-gradient(to right, ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0]} -15%, ${colors.orange[200]} 150%), ${theme.palette.mode === "dark" ? "black" : "white"}`,
                                            border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                            pr:0.5,
                                            pl:2,
                                            display:"flex",
                                            alignItems:'center',
                                            justifyContent:"space-between",
                                            gap:1.5,
                                            "&:hover": {
                                                "& > *:last-child": {
                                                    flex:"1 1 auto",
                                                    maxWidth:"100%",
                                                },
                                            },
                                        }}
                                    >
                                        <Text noBreak fontSize={13}>
                                            {t("ticket_reassigned_to")}
                                        </Text>
                                        <Box
                                            sx={{
                                                flex:"0 0 auto",
                                                height:24,
                                                px:1.5,
                                                borderRadius:3,
                                                background:colors.orange[100],
                                                lineHeight:"24px",
                                                maxWidth:130,
                                                transition:"flex 250ms, max-width 250ms",
                                            }}
                                        >
                                            <Text sx={{display:"block"}} noBreak bold variant="orange">{update.assignedTo}</Text>
                                        </Box>
                                    </Box>
                                );
                            }

                            return null;
                        })}
                    </Box>
                )}
            </Box>
        </ButtonBase>
    );
};
